'use client';

import { getToolAuthToken } from '@/lib/browser-tool-usage';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

type OutputFormat = 'png' | 'jpeg' | 'webp';
type BackgroundMode = 'original' | 'white' | 'neutral';

type PassportPhotoMakerVariant = {
  lockedPresetId?: string;
  hidePresetChooser?: boolean;
  badgeLabel?: string;
  toolSlug?: string;
  customPreset?: PassportPreset;
};

type PassportPreset = {
  id: string;
  label: string;
  width: number;
  height: number;
  printSize: string;
  hint: string;
  headMinPct: number;
  headMaxPct: number;
  eyeMinPct: number;
  eyeMaxPct: number;
};

type LoadedImage = {
  element: HTMLImageElement;
  name: string;
  type: string;
  size: number;
  width: number;
  height: number;
};

const presets: PassportPreset[] = [
  {
    id: 'us-passport',
    label: 'U.S. Passport',
    width: 600,
    height: 600,
    printSize: '2 × 2 in',
    hint: 'Square U.S. passport format with head size and eye-line guides based on official sizing.',
    headMinPct: 0.5,
    headMaxPct: 0.69,
    eyeMinPct: 0.56,
    eyeMaxPct: 0.69,
  },
  {
    id: 'uk-passport',
    label: 'UK Passport (printed)',
    width: 413,
    height: 531,
    printSize: '35 × 45 mm',
    hint: 'UK paper photo size with a larger head area and plain light background expectations.',
    headMinPct: 29 / 45,
    headMaxPct: 34 / 45,
    eyeMinPct: 0.57,
    eyeMaxPct: 0.65,
  },
  {
    // The printed 413 x 531 crop is below the floor HM Passport Office's digital
    // service accepts, so an online application needs its own preset.
    id: 'uk-passport-digital',
    label: 'UK Passport (online application)',
    width: 600,
    height: 750,
    printSize: '600 × 750 px minimum',
    hint: 'HM Passport Office digital photo: at least 600 × 750 px, plain light background.',
    headMinPct: 29 / 45,
    headMaxPct: 34 / 45,
    eyeMinPct: 0.57,
    eyeMaxPct: 0.65,
  },
  {
    id: 'canada-passport',
    label: 'Canada Passport',
    width: 591,
    height: 827,
    printSize: '50 × 70 mm',
    hint: 'Canadian passport size with a smaller head ratio and more vertical breathing room.',
    headMinPct: 31 / 70,
    headMaxPct: 36 / 70,
    eyeMinPct: 0.53,
    eyeMaxPct: 0.61,
  },
  {
    id: 'australia-passport',
    label: 'Australia Passport',
    width: 413,
    height: 531,
    printSize: '35 × 45 mm',
    hint: 'Australian passport reference size with light background and centered face guidance.',
    headMinPct: 32 / 50,
    headMaxPct: 36 / 45,
    eyeMinPct: 0.57,
    eyeMaxPct: 0.65,
  },
  {
    id: 'generic-35x45',
    label: 'Generic 35 × 45 mm',
    width: 413,
    height: 531,
    printSize: '35 × 45 mm',
    hint: 'A common ID and visa photo format when you need a standard 35 × 45 mm crop.',
    headMinPct: 0.62,
    headMaxPct: 0.74,
    eyeMinPct: 0.57,
    eyeMaxPct: 0.64,
  },
];

const outputOptions: Array<{
  value: OutputFormat;
  label: string;
  mimeType: string;
  extension: string;
  qualityEnabled: boolean;
}> = [
  { value: 'png', label: 'PNG', mimeType: 'image/png', extension: 'png', qualityEnabled: false },
  { value: 'jpeg', label: 'JPG', mimeType: 'image/jpeg', extension: 'jpg', qualityEnabled: true },
  { value: 'webp', label: 'WebP', mimeType: 'image/webp', extension: 'webp', qualityEnabled: true },
];

const backgroundModes: Array<{
  value: BackgroundMode;
  label: string;
  hint: string;
}> = [
  {
    value: 'original',
    label: 'Keep original',
    hint: 'Best when the source already has a clean white or neutral backdrop.',
  },
  {
    value: 'white',
    label: 'White cleanup',
    hint: 'Replaces a light border-connected background with white. Works best on plain bright walls.',
  },
  {
    value: 'neutral',
    label: 'Neutral cleanup',
    hint: 'Replaces a light border-connected background with a soft neutral tone.',
  },
];

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function getBaseName(fileName: string) {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function normalizeSourceLabel(type: string | null) {
  if (!type) {
    return 'Auto';
  }

  const label = type.replace('image/', '').toUpperCase();
  return label === 'JPEG' ? 'JPG' : label;
}

function getContainSize(width: number, height: number, maxWidth: number, maxHeight: number) {
  const scale = Math.min(maxWidth / width, maxHeight / height, 1);

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

async function loadImageFromFile(file: File) {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () =>
        reject(new Error('This image format is not supported in your browser yet.'));
      nextImage.src = objectUrl;
    });

    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function triggerDownload(url: string, name: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function getPlacementMetrics(
  imageWidth: number,
  imageHeight: number,
  targetWidth: number,
  targetHeight: number,
  zoom: number,
) {
  const scaleBase = Math.max(targetWidth / imageWidth, targetHeight / imageHeight);
  const scale = scaleBase * zoom;
  const renderedWidth = imageWidth * scale;
  const renderedHeight = imageHeight * scale;
  const baseX = (targetWidth - renderedWidth) / 2;
  const baseY = (targetHeight - renderedHeight) / 2;
  const minX = targetWidth - renderedWidth;
  const maxX = 0;
  const minY = targetHeight - renderedHeight;
  const maxY = 0;

  return {
    renderedWidth,
    renderedHeight,
    baseX,
    baseY,
    minX,
    maxX,
    minY,
    maxY,
  };
}

function clampOffsets(
  imageWidth: number,
  imageHeight: number,
  targetWidth: number,
  targetHeight: number,
  zoom: number,
  offsetX: number,
  offsetY: number,
) {
  const metrics = getPlacementMetrics(imageWidth, imageHeight, targetWidth, targetHeight, zoom);

  return {
    x: clamp(offsetX, metrics.minX - metrics.baseX, metrics.maxX - metrics.baseX),
    y: clamp(offsetY, metrics.minY - metrics.baseY, metrics.maxY - metrics.baseY),
  };
}

function getSmartFraming(image: LoadedImage, preset: PassportPreset) {
  const metrics = getPlacementMetrics(image.width, image.height, preset.width, preset.height, 1);
  const overflowY = Math.max(0, metrics.renderedHeight - preset.height);
  const nextOffsetY =
    image.height > image.width && overflowY > 0 ? -Math.min(overflowY * 0.08, overflowY / 3) : 0;

  return { zoom: 1, offsetX: 0, offsetY: nextOffsetY };
}

function getBackgroundColor(mode: BackgroundMode, outputFormat: OutputFormat) {
  if (mode === 'white' || outputFormat === 'jpeg') {
    return '#ffffff';
  }

  if (mode === 'neutral') {
    return '#f1efe8';
  }

  return null;
}

function blendToward(value: number, target: number, amount: number) {
  return Math.round(value + (target - value) * amount);
}

function replaceEdgeBackground(
  context: CanvasRenderingContext2D,
  replacement: { r: number; g: number; b: number },
) {
  const { width, height } = context.canvas;
  const imageData = context.getImageData(0, 0, width, height);
  const { data } = imageData;
  const sampleStep = Math.max(6, Math.floor(Math.min(width, height) / 36));
  const samples: Array<{ r: number; g: number; b: number }> = [];

  const samplePixel = (x: number, y: number) => {
    const index = (y * width + x) * 4;
    const alpha = data[index + 3];

    if (alpha < 200) {
      return;
    }

    const r = data[index];
    const g = data[index + 1];
    const b = data[index + 2];
    const brightness = (r + g + b) / 3;

    if (brightness < 150) {
      return;
    }

    samples.push({ r, g, b });
  };

  for (let x = 0; x < width; x += sampleStep) {
    samplePixel(x, 0);
    samplePixel(x, height - 1);
  }

  for (let y = 0; y < height; y += sampleStep) {
    samplePixel(0, y);
    samplePixel(width - 1, y);
  }

  if (samples.length < 8) {
    return;
  }

  const reference = samples.reduce(
    (sum, sample) => ({
      r: sum.r + sample.r,
      g: sum.g + sample.g,
      b: sum.b + sample.b,
    }),
    { r: 0, g: 0, b: 0 },
  );

  reference.r /= samples.length;
  reference.g /= samples.length;
  reference.b /= samples.length;

  const tolerance = 88;
  const brightnessFloor = 120;
  const visited = new Uint8Array(width * height);
  const queue = new Uint32Array(width * height);
  let head = 0;
  let tail = 0;

  const isBackgroundLike = (index: number) => {
    const alpha = data[index + 3];

    if (alpha < 180) {
      return false;
    }

    const r = data[index];
    const g = data[index + 1];
    const b = data[index + 2];
    const brightness = (r + g + b) / 3;
    const delta = Math.abs(r - reference.r) + Math.abs(g - reference.g) + Math.abs(b - reference.b);

    return brightness >= brightnessFloor && delta <= tolerance;
  };

  const push = (x: number, y: number) => {
    const id = y * width + x;

    if (visited[id]) {
      return;
    }

    const index = id * 4;

    if (!isBackgroundLike(index)) {
      return;
    }

    visited[id] = 1;
    queue[tail] = id;
    tail += 1;
  };

  for (let x = 0; x < width; x += sampleStep) {
    push(x, 0);
    push(x, height - 1);
  }

  for (let y = 0; y < height; y += sampleStep) {
    push(0, y);
    push(width - 1, y);
  }

  while (head < tail) {
    const id = queue[head];
    head += 1;
    const x = id % width;
    const y = Math.floor(id / width);
    const index = id * 4;
    const originalR = data[index];
    const originalG = data[index + 1];
    const originalB = data[index + 2];

    data[index] = blendToward(originalR, replacement.r, 0.94);
    data[index + 1] = blendToward(originalG, replacement.g, 0.94);
    data[index + 2] = blendToward(originalB, replacement.b, 0.94);
    data[index + 3] = 255;

    if (x > 0) {
      push(x - 1, y);
    }
    if (x + 1 < width) {
      push(x + 1, y);
    }
    if (y > 0) {
      push(x, y - 1);
    }
    if (y + 1 < height) {
      push(x, y + 1);
    }
  }

  context.putImageData(imageData, 0, 0);
}

function drawPassportPhoto(
  context: CanvasRenderingContext2D,
  image: LoadedImage,
  preset: PassportPreset,
  zoom: number,
  offsetX: number,
  offsetY: number,
  backgroundMode: BackgroundMode,
  outputFormat: OutputFormat,
) {
  const backgroundColor = getBackgroundColor(backgroundMode, outputFormat);

  context.clearRect(0, 0, preset.width, preset.height);

  if (backgroundColor) {
    context.save();
    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, preset.width, preset.height);
    context.restore();
  }

  const metrics = getPlacementMetrics(image.width, image.height, preset.width, preset.height, zoom);
  const clampedOffsets = clampOffsets(
    image.width,
    image.height,
    preset.width,
    preset.height,
    zoom,
    offsetX,
    offsetY,
  );
  const drawX = metrics.baseX + clampedOffsets.x;
  const drawY = metrics.baseY + clampedOffsets.y;

  context.drawImage(image.element, drawX, drawY, metrics.renderedWidth, metrics.renderedHeight);

  if (backgroundMode !== 'original') {
    replaceEdgeBackground(
      context,
      backgroundMode === 'white' ? { r: 255, g: 255, b: 255 } : { r: 241, g: 239, b: 232 },
    );
  }
}

function formatPercent(value: number) {
  return `${Math.round(value * 100)}%`;
}

type BrowserPassportPhotoMakerProps = {
  variant?: PassportPhotoMakerVariant;
};

export default function BrowserPassportPhotoMaker({ variant }: BrowserPassportPhotoMakerProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const previewFrameRef = useRef<HTMLDivElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const pointerDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const availablePresets = useMemo(
    () => (variant?.customPreset ? [...presets, variant.customPreset] : presets),
    [variant?.customPreset],
  );
  const lockedPreset = useMemo(() => {
    if (!variant?.lockedPresetId) {
      return null;
    }

    return availablePresets.find((preset) => preset.id === variant.lockedPresetId) ?? null;
  }, [availablePresets, variant?.lockedPresetId]);
  const [loadedImage, setLoadedImage] = useState<LoadedImage | null>(null);
  const [activePresetId, setActivePresetId] = useState<string>(
    lockedPreset?.id ?? availablePresets[0].id,
  );
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('jpeg');
  const [backgroundMode, setBackgroundMode] = useState<BackgroundMode>('white');
  const [quality, setQuality] = useState(0.96);
  const [zoom, setZoom] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isPointerDragging, setIsPointerDragging] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [previewFrameSize, setPreviewFrameSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const activePreset = useMemo(
    () => availablePresets.find((preset) => preset.id === activePresetId) ?? availablePresets[0],
    [activePresetId, availablePresets],
  );
  const selectedOutput = useMemo(
    () => outputOptions.find((option) => option.value === outputFormat) ?? outputOptions[0],
    [outputFormat],
  );
  const activeBackground = useMemo(
    () => backgroundModes.find((option) => option.value === backgroundMode) ?? backgroundModes[0],
    [backgroundMode],
  );
  const previewSize = useMemo(
    () =>
      getContainSize(
        activePreset.width,
        activePreset.height,
        Math.max(280, (previewFrameSize?.width ?? 1120) - 32),
        Math.max(320, (previewFrameSize?.height ?? 760) - 32),
      ),
    [activePreset.height, activePreset.width, previewFrameSize],
  );
  const averageHeadPct = (activePreset.headMinPct + activePreset.headMaxPct) / 2;
  const averageEyePct = (activePreset.eyeMinPct + activePreset.eyeMaxPct) / 2;
  const chinPctFromBottom = clamp(averageEyePct - averageHeadPct * 0.62, 0.12, 0.32);
  const crownPctFromBottom = clamp(chinPctFromBottom + averageHeadPct, 0.66, 0.9);
  const crownLineTop = 100 - crownPctFromBottom * 100;
  const chinLineTop = 100 - chinPctFromBottom * 100;
  const eyeBandTop = 100 - activePreset.eyeMaxPct * 100;
  const eyeBandHeight = (activePreset.eyeMaxPct - activePreset.eyeMinPct) * 100;

  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  useEffect(() => {
    if (!previewFrameRef.current) {
      return;
    }

    const frame = previewFrameRef.current;
    const syncFrameSize = () => {
      setPreviewFrameSize({
        width: frame.clientWidth,
        height: frame.clientHeight,
      });
    };

    syncFrameSize();

    const observer = new ResizeObserver(() => syncFrameSize());
    observer.observe(frame);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!lockedPreset) {
      return;
    }

    setActivePresetId(lockedPreset.id);
  }, [lockedPreset]);

  useEffect(() => {
    if (!loadedImage) {
      return;
    }

    const nextFrame = getSmartFraming(loadedImage, activePreset);
    setZoom(nextFrame.zoom);
    setOffsetX(nextFrame.offsetX);
    setOffsetY(nextFrame.offsetY);
  }, [activePreset, loadedImage]);

  useEffect(() => {
    if (outputFormat === 'jpeg' && backgroundMode === 'original') {
      setBackgroundMode('white');
    }
  }, [backgroundMode, outputFormat]);

  useEffect(() => {
    const canvas = previewCanvasRef.current;

    if (!canvas || !loadedImage) {
      return;
    }

    const devicePixelRatio = window.devicePixelRatio || 1;
    canvas.width = Math.round(previewSize.width * devicePixelRatio);
    canvas.height = Math.round(previewSize.height * devicePixelRatio);

    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.scale(devicePixelRatio, devicePixelRatio);

    drawPassportPhoto(
      context,
      loadedImage,
      {
        ...activePreset,
        width: previewSize.width,
        height: previewSize.height,
      },
      zoom,
      offsetX * (previewSize.width / activePreset.width),
      offsetY * (previewSize.height / activePreset.height),
      backgroundMode,
      outputFormat,
    );
  }, [
    activePreset,
    backgroundMode,
    loadedImage,
    offsetX,
    offsetY,
    outputFormat,
    previewSize.height,
    previewSize.width,
    zoom,
  ]);

  function resetOutputState() {
    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
    }

    setDownloadUrl(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);
    setIsLoadingImage(true);

    if (!file) {
      setLoadedImage(null);
      setIsLoadingImage(false);
      return;
    }

    try {
      const image = await loadImageFromFile(file);
      const nextLoadedImage = {
        element: image,
        name: file.name,
        type: file.type,
        size: file.size,
        width: image.naturalWidth,
        height: image.naturalHeight,
      };

      setLoadedImage(nextLoadedImage);
      const nextFrame = getSmartFraming(nextLoadedImage, activePreset);
      setZoom(nextFrame.zoom);
      setOffsetX(nextFrame.offsetX);
      setOffsetY(nextFrame.offsetY);
    } catch (error) {
      setLoadedImage(null);
      setErrorMessage(error instanceof Error ? error.message : 'Could not read this image.');
    } finally {
      setIsLoadingImage(false);
    }
  }

  function handlePresetChange(presetId: string) {
    setActivePresetId(presetId);
    resetOutputState();
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!loadedImage) {
      return;
    }

    resetOutputState();
    pointerDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX,
      offsetY,
    };
    setIsPointerDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (
      !loadedImage ||
      !pointerDragRef.current ||
      pointerDragRef.current.pointerId !== event.pointerId
    ) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const scaleX = activePreset.width / rect.width;
    const scaleY = activePreset.height / rect.height;
    const nextOffsets = clampOffsets(
      loadedImage.width,
      loadedImage.height,
      activePreset.width,
      activePreset.height,
      zoom,
      pointerDragRef.current.offsetX + (event.clientX - pointerDragRef.current.startX) * scaleX,
      pointerDragRef.current.offsetY + (event.clientY - pointerDragRef.current.startY) * scaleY,
    );

    setOffsetX(nextOffsets.x);
    setOffsetY(nextOffsets.y);
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!pointerDragRef.current || pointerDragRef.current.pointerId !== event.pointerId) {
      return;
    }

    pointerDragRef.current = null;
    setIsPointerDragging(false);

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  function resetFraming() {
    if (!loadedImage) {
      return;
    }

    const nextFrame = getSmartFraming(loadedImage, activePreset);
    setZoom(nextFrame.zoom);
    setOffsetX(nextFrame.offsetX);
    setOffsetY(nextFrame.offsetY);
    resetOutputState();
  }

  async function runExport() {
    if (!loadedImage) {
      setErrorMessage('Choose a photo first.');
      return;
    }

    setIsExporting(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: variant?.toolSlug ?? 'passport-photo-maker' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another export right now.';
        throw new Error(message);
      }

      const canvas = document.createElement('canvas');
      canvas.width = activePreset.width;
      canvas.height = activePreset.height;
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Your browser could not prepare the export.');
      }

      drawPassportPhoto(
        context,
        loadedImage,
        activePreset,
        zoom,
        offsetX,
        offsetY,
        backgroundMode,
        outputFormat,
      );

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(
          (nextBlob) => resolve(nextBlob),
          selectedOutput.mimeType,
          selectedOutput.qualityEnabled ? quality : undefined,
        );
      });

      if (!blob) {
        throw new Error('The export could not finish for this photo.');
      }

      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }

      const nextDownloadUrl = URL.createObjectURL(blob);
      const nextFileName = `${getBaseName(loadedImage.name)}-${activePreset.id}.${selectedOutput.extension}`;

      setDownloadUrl(nextDownloadUrl);
      setResultSize(blob.size);
      triggerDownload(nextDownloadUrl, nextFileName);
      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Processed locally in your browser. Unlimited plan active.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `Processed locally in your browser. ${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : 'Processed locally in your browser.',
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'The photo could not be exported.');
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Passport-style photo maker in the browser</h2>
          <p>
            {variant?.hidePresetChooser
              ? `Upload a portrait, line it up for ${activePreset.label}, and export a clean crop without server-side AI or cloud processing.`
              : 'Upload a portrait, choose the country format, line it up against the document guides, and export a clean crop without server-side AI or cloud processing.'}
          </p>
        </div>
        <span className="tool-live-badge">{variant?.badgeLabel ?? 'Local processing'}</span>
      </div>

      {variant?.hidePresetChooser ? null : (
        <div className="tool-live-routes" aria-label="Passport and ID photo presets">
          {availablePresets.map((preset) => (
            <button
              key={preset.id}
              className={`tool-live-route ${activePresetId === preset.id ? 'tool-live-route-active' : ''}`}
              onClick={() => handlePresetChange(preset.id)}
              type="button"
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {loadedImage ? loadedImage.name : 'Choose a portrait photo or drop it here'}
              </strong>
              <span>
                {loadedImage
                  ? `${formatFileSize(loadedImage.size)} • ${loadedImage.type || 'Image file'}`
                  : 'Best with a front-facing portrait on a plain light background'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/*"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            {variant?.hidePresetChooser ? null : (
              <label className="field">
                <span>Preset</span>
                <select
                  onChange={(event) => handlePresetChange(event.target.value)}
                  value={activePresetId}
                >
                  {availablePresets.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {`${preset.label} • ${preset.printSize}`}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="field">
              <span>Background</span>
              <select
                onChange={(event) => {
                  setBackgroundMode(event.target.value as BackgroundMode);
                  resetOutputState();
                }}
                value={backgroundMode}
              >
                {backgroundModes.map((mode) => (
                  <option
                    disabled={outputFormat === 'jpeg' && mode.value === 'original'}
                    key={mode.value}
                    value={mode.value}
                  >
                    {mode.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Format</span>
              <select
                onChange={(event) => {
                  setOutputFormat(event.target.value as OutputFormat);
                  resetOutputState();
                }}
                value={outputFormat}
              >
                {outputOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Print size</span>
              <input readOnly type="text" value={activePreset.printSize} />
            </label>
          </div>

          <div className="tool-live-preview tool-editor-preview tool-passport-editor-shell">
            <div className="tool-image-editor-header">
              <h3>Preview</h3>
              <div className="tool-image-editor-header-actions">
                <button
                  className="button button-ghost tool-image-editor-revert"
                  disabled={!loadedImage}
                  onClick={resetFraming}
                  type="button"
                >
                  <span>Reset framing</span>
                </button>
              </div>
            </div>
            <div
              ref={previewFrameRef}
              className="tool-live-preview-frame tool-editor-preview-frame tool-passport-editor-frame"
            >
              {loadedImage ? (
                <div
                  className="tool-passport-editor-stage"
                  aria-label={`${activePreset.label} passport photo preview`}
                  style={{
                    aspectRatio: `${previewSize.width} / ${previewSize.height}`,
                    width: `${previewSize.width}px`,
                    height: `${previewSize.height}px`,
                  }}
                >
                  <canvas
                    ref={previewCanvasRef}
                    className={`tool-editor-canvas tool-passport-editor-canvas ${isPointerDragging ? 'tool-passport-editor-canvas-dragging' : ''}`}
                    onPointerCancel={handlePointerUp}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                  />
                  <div className="tool-passport-guides" aria-hidden="true">
                    <div
                      className="tool-passport-guide-eye-band"
                      style={{ top: `${eyeBandTop}%`, height: `${eyeBandHeight}%` }}
                    />
                    <div
                      className="tool-passport-guide-line tool-passport-guide-line-crown"
                      style={{ top: `${crownLineTop}%` }}
                    />
                    <div
                      className="tool-passport-guide-line tool-passport-guide-line-chin"
                      style={{ top: `${chinLineTop}%` }}
                    />
                    <div className="tool-passport-guide-line tool-passport-guide-line-center" />
                    <div className="tool-passport-guide-safe-area" />
                  </div>
                </div>
              ) : (
                <div className="tool-editor-empty">
                  <strong>{isLoadingImage ? 'Preparing preview…' : 'Preview appears here'}</strong>
                  <span>Upload a front-facing portrait to align it with the document guides.</span>
                </div>
              )}
            </div>
            <div className="tool-passport-editor-meta">
              <span>{activePreset.hint}</span>
              <span>Everything is processed locally in your browser.</span>
            </div>
          </div>

          <div className="tool-live-quality-shell tool-editor-sliders">
            <label className="field tool-live-quality">
              <span>Zoom</span>
              <input
                max="2.5"
                min="1"
                onChange={(event) => {
                  setZoom(Number(event.target.value));
                  resetOutputState();
                }}
                step="0.01"
                type="range"
                value={zoom}
              />
              <small>{zoom.toFixed(2)}×</small>
            </label>
            <label className="field tool-live-quality">
              <span>Guide target</span>
              <input
                readOnly
                type="text"
                value={`${formatPercent(activePreset.headMinPct)}–${formatPercent(activePreset.headMaxPct)} head height`}
              />
              <small>{`Eyes guide: ${formatPercent(activePreset.eyeMinPct)}–${formatPercent(activePreset.eyeMaxPct)} from the bottom`}</small>
            </label>
            <label
              className={`field tool-live-quality ${selectedOutput.qualityEnabled ? '' : 'tool-live-quality-disabled'}`}
            >
              <span>Quality</span>
              <input
                disabled={!selectedOutput.qualityEnabled}
                max="0.98"
                min="0.65"
                onChange={(event) => {
                  setQuality(Number(event.target.value));
                  resetOutputState();
                }}
                step="0.01"
                type="range"
                value={quality}
              />
              <small>
                {selectedOutput.qualityEnabled
                  ? `Used for ${selectedOutput.label} exports`
                  : 'PNG keeps a lossless-style export for print and further editing'}
              </small>
            </label>
            <label className="field tool-live-quality">
              <span>Background cleanup</span>
              <input readOnly type="text" value={activeBackground.label} />
              <small>{activeBackground.hint}</small>
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!loadedImage || isExporting}
              onClick={() => void runExport()}
              type="button"
            >
              {isExporting ? 'Exporting…' : `Export as ${selectedOutput.label}`}
            </button>
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Passport photo setup</h3>
          <div className="tool-mockup-chip-list">
            <span>{activePreset.label}</span>
            <span>{activePreset.printSize}</span>
            <span>{selectedOutput.label}</span>
            <span>{activeBackground.label}</span>
          </div>
          <p>{activePreset.hint}</p>
          <div className="tool-live-panel-note">
            <strong>Local-only workflow</strong>
            <span>
              No server-side AI is used here. Cropping, positioning, guide overlays, and light
              background cleanup all run inside the browser on your device.
            </span>
          </div>
          {loadedImage ? (
            <div className="tool-live-stats">
              <div>
                <strong>Source</strong>
                <span>{formatFileSize(loadedImage.size)}</span>
              </div>
              <div>
                <strong>Type</strong>
                <span>{normalizeSourceLabel(loadedImage.type)}</span>
              </div>
              <div>
                <strong>Source size</strong>
                <span>{`${loadedImage.width} × ${loadedImage.height}`}</span>
              </div>
              <div>
                <strong>Export size</strong>
                <span>{`${activePreset.width} × ${activePreset.height}`}</span>
              </div>
              <div>
                <strong>Head guide</strong>
                <span>{`${formatPercent(activePreset.headMinPct)}–${formatPercent(activePreset.headMaxPct)}`}</span>
              </div>
              {resultSize ? (
                <div>
                  <strong>Result</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </aside>
      </div>
    </article>
  );
}
